import { AuthenticationResult, EventMessage, EventType, PopupRequest, RedirectRequest, SsoSilentRequest } from '@azure/msal-browser';
import { AuthEvent, B2cAuthorityTypeEnum, IAuthService } from '@vivli/core/infrastructure/interface';
import { BehaviorSubject } from 'rxjs';
import { LoggerService } from '@vivli/shared/infrastructure/service';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMsalOptions } from './use-msal-options.hook';
import { useStorage } from '@vivli/shared/infrastructure/hook';
import { useMsal } from '@azure/msal-react';
import { AuthError } from '@azure/msal-browser';
import { useLogoutOnClose } from './logout-on-close.hook';

export const useAuthService = (loggerKey = 'authService'): IAuthService => {
    const { options, isPartnerLogin } = useMsalOptions();
    const inProgressRef = useRef(false);
    const authEventsRef = useRef(new BehaviorSubject<AuthEvent>('auth-initialized'));
    const { setItem, getItem, deleteItem } = useStorage();
    const { initializeBrowserActivityTimer, checkIfLogoutNeeded, disableTimer } = useLogoutOnClose();

    const { instance } = useMsal();

    instance.enableAccountStorageEvents();

    const getAccount = () => {
        return instance.getActiveAccount();
    };

    const setLoginHint = (account: any) => {
        const email = account.idTokenClaims?.['email'];
        if (email) {
            setItem('loginHint', email, 'local');
        }
    };

    const getLoginHint = () => {
        return getItem<string>('loginHint', 'local');
    };

    const getAuthorityUri = useCallback(
        (authorityType?: B2cAuthorityTypeEnum): string => {
            if (!options.b2cOptions || !options.b2cOptions.authorities) return null;

            const authPolicyName = options.b2cOptions?.authorities[authorityType] || options.b2cOptions?.authorities.default;
            const b2cTenant = options.b2cOptions.tenant;
            const b2cName = b2cTenant.split('.')[0];

            return `https://${b2cName}.b2clogin.com/${b2cTenant}/${authPolicyName}`;
        },
        [options.b2cOptions]
    );

    const handleAuthResult = useCallback((response: AuthenticationResult) => {
        if (response) {
            authEventsRef.current.next('token-refreshed');
            return response.idToken;
        }
    }, []);

    const getTokenSilent = useCallback(async () => {
        const account = getAccount();
        if (!account) return null;

        return instance
            .acquireTokenSilent({
                scopes: options.b2cOptions.scopes,
                account: account
            })
            .then(handleAuthResult)
            .catch((error) => {
                instance.setActiveAccount(null);
                authEventsRef.current.next('silent-login-failure');

                return null;
            });
    }, [instance, options.b2cOptions.scopes, handleAuthResult]);

    const getAccountSilent = useCallback(async () => {
        inProgressRef.current = true;
        authEventsRef.current.next('silent-login');

        return instance
            .acquireTokenSilent({
                scopes: options.b2cOptions.scopes,
                account: getAccount()
            })
            .then((result) => {
                instance.setActiveAccount(result.account);
                authEventsRef.current.next('login-success');
                initializeBrowserActivityTimer();
                inProgressRef.current = false;
            })
            .catch(() => {
                instance.setActiveAccount(null);
                authEventsRef.current.next('login-failure');
                inProgressRef.current = false;
            });
    }, [instance, options.b2cOptions.scopes, handleAuthResult]);

    const getTokenPopup = useCallback(async () => {
        return instance
            .acquireTokenPopup({
                scopes: options.b2cOptions.scopes,
                account: getAccount()
            })
            .then(handleAuthResult);
    }, [instance, options.b2cOptions.scopes, handleAuthResult]);

    const handleAuthResponse = useCallback(
        (response: AuthenticationResult, successEvent: AuthEvent, failureEvent: AuthEvent, logMessage: string) => {
            if (response) {
                setLoginHint(response.account);
                instance.setActiveAccount(response.account);
                authEventsRef.current.next(successEvent);
                inProgressRef.current = true;
                return response;
            } else {
                authEventsRef.current.next(failureEvent);
                inProgressRef.current = false;
                return null;
            }
        },
        [instance]
    );

    const handleLoginPopup = useCallback(
        async (authority: B2cAuthorityTypeEnum, successEvent: AuthEvent, failureEvent: AuthEvent, logMessage: string) => {
            const request: PopupRequest = {
                scopes: options.b2cOptions.scopes,
                authority: getAuthorityUri(authority),
                prompt: 'select_account'
            };

            inProgressRef.current = true;
            return instance
                .loginPopup(request)
                .then((response) => handleAuthResponse(response, successEvent, failureEvent, logMessage))
                .catch((error: AuthError) => {
                    if (error.message.includes('AADB2C90091') || error.message.includes('user_cancelled')) {
                        authEventsRef.current.next('cancelled');
                        return;
                    } else if (error.message.includes('AADB2C90118')) {
                        // switch to change password flow
                        return updatePassword();
                    }

                    authEventsRef.current.next(failureEvent);
                    LoggerService.debug(logMessage, error, loggerKey);
                    inProgressRef.current = false;
                });
        },
        [instance, options.b2cOptions.scopes, getAuthorityUri, handleAuthResponse]
    );

    const handleLoginRedirect = useCallback(
        async (authority: B2cAuthorityTypeEnum, successEvent: AuthEvent, failureEvent: AuthEvent, logMessage: string) => {
            const request: RedirectRequest = {
                scopes: options.b2cOptions.scopes,
                authority: getAuthorityUri(authority),
                prompt: 'select_account'
            };

            inProgressRef.current = true;
            return instance.loginRedirect(request).catch((error) => {
                authEventsRef.current.next(failureEvent);
                LoggerService.debug(logMessage, error, loggerKey);
            });
        },
        [instance, options.b2cOptions.scopes, getAuthorityUri]
    );

    const logout = useCallback(
        async (redirectUrl?: string) => {
            authEventsRef.current.next('logout');
            inProgressRef.current = true;
            deleteItem('loginHint', 'local');
            return instance
                .logoutRedirect({
                    postLogoutRedirectUri: redirectUrl
                })
                .then(() => {
                    instance.setActiveAccount(null);
                    authEventsRef.current.next('logout-success');
                    inProgressRef.current = false;
                })
                .catch((error) => {
                    LoggerService.error('Logout failed: ', error, loggerKey);
                    authEventsRef.current.next('logout-failure');
                    inProgressRef.current = false;
                });
        },
        [instance]
    );

    const login = useCallback(async () => {
        authEventsRef.current.next('login');

        if (isPartnerLogin) {
            return loginRedirect();
        }

        return handleLoginPopup('signInOnly', 'login-success', 'login-failure', 'User login failed: ');
    }, [handleLoginPopup, isPartnerLogin]);

    const loginRedirect = useCallback(async () => {
        authEventsRef.current.next('login');

        return handleLoginRedirect('sso', 'login-success', 'login-failure', 'User login failed: ');
    }, [handleLoginRedirect, isPartnerLogin]);

    const loginSilent = useCallback(async (): Promise<boolean> => {
        const loginHint = getLoginHint();

        // no login hint found, don't attempt silent login as we don't know what user to login as
        if (!loginHint) {
            // ensure we still throw the event to cancel the loading screen
            authEventsRef.current.next('login-failure');
            return false;
        }

        const request: SsoSilentRequest = {
            scopes: options.b2cOptions.scopes,
            authority: getAuthorityUri(),
            loginHint
        };

        authEventsRef.current.next('silent-login');
        inProgressRef.current = true;
        return instance
            .ssoSilent(request)
            .then((response) => {
                inProgressRef.current = false;
                if (response) {
                    instance.setActiveAccount(response.account);
                    authEventsRef.current.next('login-success');
                    initializeBrowserActivityTimer();
                    return true;
                }
                authEventsRef.current.next('login-failure');
                return false;
            })
            .catch((error: AuthError) => {
                if (error.message.includes('interaction_required')) {
                    // do nothing, we don't force logins
                    authEventsRef.current.next('login-failure');
                }
                inProgressRef.current = false;
                return false;
            });
    }, [instance, options.b2cOptions.scopes, getAuthorityUri]);

    const signUp = useCallback(async () => {
        // show the "Signing up..." overlay while the sign-up popup is active
        authEventsRef.current.next('signup');
        const response = await handleLoginPopup('signUpOnly', 'signup-success', 'signup-failure', 'User signup failed: ');

        // Sign-up authenticates against the sign-up policy (e.g. B2C_1A_SignUp), so the account and
        // tokens are cached under that authority. All silent token acquisition (page refresh, API
        // calls) defaults to the sign-in policy, which would not match - leaving the user unable to
        // stay logged in. Establish a session under the sign-in policy now. Prefer silent SSO; if the
        // B2C session is not shared across policies, fall back to an interactive login.
        if (!response) {
            return;
        }

        // 'signup-success' hid the "Signing up..." overlay; show the "Logging in..." overlay while we
        // establish the session under the sign-in policy.
        authEventsRef.current.next('login');
        const loggedIn = await loginSilent();
        if (!loggedIn) {
            return login();
        }
    }, [handleLoginPopup, loginSilent, login]);

    const updatePassword = useCallback(async () => {
        authEventsRef.current.next('password-update');
        return handleLoginPopup('forgotPassword', 'password-update-success', 'password-update-failure', 'Password update failed: ');
    }, [handleLoginPopup]);

    const updateProfile = useCallback(async () => {
        authEventsRef.current.next('profile-update');
        return handleLoginPopup('editProfile', 'profile-update-success', 'profile-update-failure', 'Profile update failed: ');
    }, [handleLoginPopup]);

    useEffect(() => {
        const callbackId = instance.addEventCallback((message: EventMessage) => {
            if (message.eventType === EventType.ACCOUNT_REMOVED) {
                logout();
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance, logout]);

    useEffect(() => {
        if (inProgressRef.current) return;

        if (checkIfLogoutNeeded()) {
            disableTimer();
            logout();
        } else {
            const accounts = instance.getAllAccounts();

            if (accounts.length > 0) {
                getAccountSilent();
            } else {
                loginSilent();
            }
        }
    }, []);

    return useMemo(
        () => ({
            getTokenSilent,
            getTokenPopup,
            login,
            loginSilent,
            signUp,
            logout,
            updatePassword,
            updateProfile,
            getAccount,
            instance,
            authEvents: authEventsRef.current
        }),
        [
            getTokenSilent,
            getTokenPopup,
            login,
            loginSilent,
            signUp,
            logout,
            updatePassword,
            updateProfile,
            getAccount,
            instance,
            authEventsRef
        ]
    );
};
